/**
 * OutlineBuilder — creates a volume's Heading-1 outline two ways:
 *   1. AI-generate (POST .../outline/generate-ai → review → POST .../confirm-ai)
 *   2. Upload & parse a .docx/.pdf (POST .../outline/parse-upload → review → POST .../confirm-parsed)
 * Both paths land on the same reviewable/editable heading list before
 * anything is persisted — nothing commits until the user clicks Confirm.
 */
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Sparkles, Upload, Loader2, X, GripVertical, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OutlineBuilder({ pursuitId, volumeId, volumeLabel, open, onOpenChange, onCommitted }) {
  const { getAuthHeaders } = useAuth();
  const [mode, setMode] = useState(null); // null | 'ai' | 'upload'
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState(null); // [{heading, confidence?, mapped_section_references?}]
  const [uploadedFile, setUploadedFile] = useState(null);
  const [rtmContextUsed, setRtmContextUsed] = useState(null);

  const reset = () => {
    setMode(null);
    setProposed(null);
    setUploadedFile(null);
    setRtmContextUsed(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const runAiGenerate = async () => {
    setMode('ai');
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/volumes/${volumeId}/outline/generate-ai`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generation failed');
      }
      const data = await res.json();
      setProposed(data.proposed_headings.map((h) => ({ ...h })));
      setRtmContextUsed(data.rtm_context_used);
    } catch (e) {
      toast.error(e.message || 'AI outline generation failed');
      setMode(null);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(
    async (files) => {
      const file = files[0];
      if (!file) return;
      setMode('upload');
      setUploadedFile(file);
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(
          `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/volumes/${volumeId}/outline/parse-upload`,
          { method: 'POST', headers: getAuthHeaders(), body: fd }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Could not parse headings from this file');
        }
        const data = await res.json();
        setProposed(data.proposed_headings.map((h) => ({ ...h })));
      } catch (e) {
        toast.error(e.message || 'Upload parse failed');
        setMode(null);
      } finally {
        setLoading(false);
      }
    },
    [pursuitId, volumeId, getAuthHeaders]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
    },
  });

  const updateHeading = (idx, text) => {
    setProposed((prev) => prev.map((h, i) => (i === idx ? { ...h, heading: text } : h)));
  };
  const removeHeading = (idx) => {
    setProposed((prev) => prev.filter((_, i) => i !== idx));
  };
  const moveHeading = (idx, dir) => {
    setProposed((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const confirm = async () => {
    if (!proposed || proposed.length === 0) {
      toast.error('Add at least one heading');
      return;
    }
    setLoading(true);
    try {
      let res;
      if (mode === 'ai') {
        res = await fetch(
          `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/volumes/${volumeId}/outline/confirm-ai`,
          {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              headings: proposed.map((h) => ({
                heading: h.heading,
                mapped_section_references: h.mapped_section_references || [],
              })),
            }),
          }
        );
      } else {
        const fd = new FormData();
        fd.append('headings', JSON.stringify(proposed.map((h) => ({ heading: h.heading }))));
        if (uploadedFile) fd.append('file', uploadedFile);
        res = await fetch(
          `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/volumes/${volumeId}/outline/confirm-parsed`,
          { method: 'POST', headers: getAuthHeaders(), body: fd }
        );
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Outline created — ${data.sections.length} section${data.sections.length > 1 ? 's' : ''}`);
      onCommitted?.(data.sections);
      close();
    } catch {
      toast.error('Failed to save outline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-slate-200 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-400" />
            Build Outline — {volumeLabel}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generate with AI or upload a predetermined outline. Review before it's saved.
          </DialogDescription>
        </DialogHeader>

        {!mode && (
          <div className="grid grid-cols-1 gap-3 py-2">
            <Button onClick={runAiGenerate} className="justify-start h-auto py-3" data-testid="outline-ai-generate-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              <div className="text-left">
                <div className="text-sm font-medium">AI Generate Outline</div>
                <div className="text-xs opacity-80">Proposes headings from RTM requirements + eval factors</div>
              </div>
            </Button>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-500'
              }`}
              data-testid="outline-upload-dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
              <p className="text-sm">Upload Outline Document</p>
              <p className="text-xs text-slate-500 mt-1">.docx or .pdf — drag & drop or click</p>
            </div>
          </div>
        )}

        {mode && loading && (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-400" />
            <p className="text-sm text-slate-400">
              {mode === 'ai' ? 'Generating outline…' : 'Parsing document…'}
            </p>
          </div>
        )}

        {mode && !loading && proposed && (
          <div className="space-y-3 py-2">
            {mode === 'ai' && rtmContextUsed === false && (
              <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                No RTM requirements are mapped to this volume yet — this outline is based on evaluation
                factors and volume notes alone. Run RTM AI-mapping first for a more precise result.
              </p>
            )}
            <p className="text-xs text-slate-400">
              Review, edit, reorder, or remove before saving ({proposed.length} heading{proposed.length !== 1 ? 's' : ''}).
            </p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {proposed.map((h, idx) => (
                <div key={idx} className="flex items-center gap-1.5 bg-slate-800/60 rounded p-1.5">
                  <div className="flex flex-col">
                    <button onClick={() => moveHeading(idx, -1)} className="text-slate-500 hover:text-slate-300" disabled={idx === 0}>
                      <GripVertical className="w-3 h-3" />
                    </button>
                  </div>
                  <Input
                    value={h.heading}
                    onChange={(e) => updateHeading(idx, e.target.value)}
                    className="h-7 text-xs bg-slate-900 border-slate-700 flex-1"
                  />
                  {h.confidence === 'low' && (
                    <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400">
                      low confidence
                    </Badge>
                  )}
                  <button onClick={() => removeHeading(idx)} className="text-slate-500 hover:text-red-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {mode && !loading && proposed && (
            <>
              <Button variant="outline" onClick={reset}>
                Back
              </Button>
              <Button onClick={confirm} disabled={loading} data-testid="outline-confirm-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Confirm & Create Outline
              </Button>
            </>
          )}
          {!mode && (
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
